<script lang="ts">
interface Props {
    modelValue: boolean
    report: Report
}
</script>

<script setup lang="ts">
import type { ResourceResponse } from '@/types/general'

import { watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import useForm from '@/hooks/use-form'
import { useAuthStore } from '@/stores/auth-store'
import { Report, TourType } from '@/types/driver-report'
import { emitter } from '@/utils/mitt'
import { DropdownOption } from '@/types/inputs'

import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyModal from '@/components/my-components/MyModal.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'

interface Form extends Record<string, unknown> {
    tourType: TourType | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
    (e: 'close'): void
    (e: 'update:modelValue', value: boolean): void
}>()

const authStore = useAuthStore()
const { t } = useI18n()

const reportTypes = computed<DropdownOption[]>(() => [
    { label: t('domestic'), value: TourType.Domestic },
    { label: t('export'), value: TourType.Export },
    { label: t('domesticAfterExport'), value: TourType.DomesticAfterExport },
])

const { data, submit, errors, reset, loading } = useForm<Form>({
    tourType: props.report.tourType,
})

async function onSubmit() {
    const response = await submit<ResourceResponse<Report>>(
        'PUT',
        window.route('dr.company.reports.update', {
            company: authStore.companyId,
            report: props.report,
        }),
    )

    if (response !== undefined) {
        emit('close')
        emitter.emit('fetchDriverReports')
    }
}

watch(
    () => props.modelValue,
    () => {
        if (props.modelValue) {
            reset({ tourType: props.report.tourType })
        }
    },
)
</script>

<template>
    <MyModal :value="props.modelValue" :max-width="550" @close="emit('close')">
        <LoaderWrapper :visible="loading" class="rounded-xl" />
        <template #title>
            {{ t('updateEntity', { entity: t('report') }) }}
        </template>
        <MyForm :errors="errors" @submit.prevent="onSubmit">
            <MySelect
                v-model="data.tourType"
                name="tourType"
                :label="t('reportType')"
                :options="reportTypes"
            />
            <div class="mt-4 flex justify-end gap-2">
                <MyButton type="button" @click="emit('update:modelValue', false)">
                    {{ t('cancel') }}
                </MyButton>
                <MyButton
                    :disabled="loading"
                    scheme="primary"
                    v-text="t('updateEntity', { entity: t('report') })"
                />
            </div>
        </MyForm>
    </MyModal>
</template>
